import { useCallback, useEffect, useState } from 'react';
import { ALL_USER_ROLES } from '@cnapse/shared/data.types.js';
import type { BillingNotificationResponse } from '@cnapse/shared/schemas.js';
import { useAuth } from '../context/AppContext';
import { useSubdomain } from '../contexts/SubdomainContext';
import { appNavigate } from '../utils/appNavigate';
import { buildTenantPath } from '../utils/tenantNavigation';
import { tenantNotificationsService } from '../services/TenantNotificationsService';
import { tenantBillingService } from '../services/TenantBillingService';

export function formatSubscriptionToolbarCompact(
  notification: BillingNotificationResponse,
): string {
  if (notification.daysRemaining != null) {
    const days = notification.daysRemaining;
    if (days === 0) {
      return 'Subscription ends today!';
    }
    if (days === 1) {
      return 'Subscription ends in 1 day!';
    }
    return `Subscription ends in ${days} days!`;
  }

  const firstSentence = notification.message.split(/[.!?]/)[0]?.trim() ?? notification.message;
  if (firstSentence.length <= 40) {
    return firstSentence.endsWith('!') ? firstSentence : `${firstSentence}!`;
  }
  return `${firstSentence.slice(0, 38)}…`;
}

export function formatSubscriptionToolbarDetail(
  notification: BillingNotificationResponse,
  isBillingAdmin: boolean,
): string {
  const daysPart =
    notification.daysRemaining != null
      ? `${notification.daysRemaining} ${notification.daysRemaining === 1 ? 'Day' : 'Days'} Remaining`
      : null;
  const message = isBillingAdmin
    ? notification.message
    : `${notification.message} Contact your administrator to manage billing.`;
  return daysPart ? `${daysPart} — ${message}` : message;
}

export function useSubscriptionNotification(): {
  notification: BillingNotificationResponse | null;
  loading: boolean;
  isBillingAdmin: boolean;
  toolbarCompactLine: string | null;
  toolbarDetailLine: string | null;
  showActionButton: boolean;
  openingPortal: boolean;
  actionError: string | null;
  handleAction: () => Promise<void>;
} {
  const [notification, setNotification] = useState<BillingNotificationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const { user } = useAuth();
  const { subdomain, isActive, isLoading: subscriptionLoading } = useSubdomain();

  const isBillingAdmin = user?.role === ALL_USER_ROLES.tenant_admin;
  const billingReturnUrl = `${window.location.origin}${buildTenantPath('/billing', subdomain)}`;

  const loadNotification = useCallback(async (): Promise<void> => {
    try {
      const data = await tenantNotificationsService.getSubscriptionNotification();
      setNotification(data);
    } catch (err) {
      console.error('Failed to load subscription notification:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (subscriptionLoading || !isActive) {
      setLoading(false);
      setNotification(null);
      return;
    }

    void loadNotification();

    const interval = window.setInterval(() => {
      void loadNotification();
    }, 5 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [isActive, subscriptionLoading, loadNotification]);

  const handleAction = useCallback(async (): Promise<void> => {
    if (!notification?.actionUrl) {
      return;
    }

    setActionError(null);

    try {
      setOpeningPortal(true);
      const portal = await tenantBillingService.createPortalSession(billingReturnUrl);
      window.location.href = portal.url;
    } catch (err) {
      console.error('Failed to open billing portal:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to open billing portal');
      appNavigate(buildTenantPath(notification.actionUrl, subdomain));
    } finally {
      setOpeningPortal(false);
    }
  }, [billingReturnUrl, notification?.actionUrl, subdomain]);

  const toolbarCompactLine =
    notification?.show && notification.message
      ? formatSubscriptionToolbarCompact(notification)
      : null;
  const toolbarDetailLine =
    notification?.show && notification.message
      ? formatSubscriptionToolbarDetail(notification, isBillingAdmin)
      : null;

  return {
    notification,
    loading,
    isBillingAdmin,
    toolbarCompactLine,
    toolbarDetailLine,
    showActionButton: isBillingAdmin && Boolean(notification?.actionUrl),
    openingPortal,
    actionError,
    handleAction,
  };
}
